import { DepositStatus } from '@principle-theorem/principle-core/interfaces';
import { DEPOSIT_REQUIRED_COLOUR_CLASSES } from './deposit-required-display';

/**
 * Exhaustive mapping of DepositStatus to display label.
 * TypeScript enforces all enum members are present.
 */
export const DEPOSIT_STATUS_LABELS: Record<DepositStatus, string> = {
  [DepositStatus.Required]: 'Required',
  [DepositStatus.Pending]: 'Pending',
  [DepositStatus.Authorised]: 'Authorised',
  [DepositStatus.Paid]: 'Paid',
  [DepositStatus.Forfeited]: 'Forfeited',
  [DepositStatus.Refunded]: 'Refunded',
  [DepositStatus.Waived]: 'Waived',
  [DepositStatus.Cancelled]: 'Cancelled',
  [DepositStatus.Released]: 'Released',
};

/**
 * Exhaustive mapping of DepositStatus to Tailwind chip classes.
 * TypeScript enforces all enum members are present.
 *
 * `Authorised` deliberately uses an amber outline rather than the solid green
 * of `Paid`. An authorisation is only a hold on the patient's card - the funds
 * have not been captured, and Stripe releases the hold after at most seven
 * days. Rendering it as a success chip lets a practice treat an appointment as
 * paid when nothing has actually been collected.
 *
 * No status here is red. Red is reserved for a deposit someone has to act on,
 * which the chip's red attention marker signals on top of the status. A red
 * status would send a receptionist scanning a column after rows that need
 * nothing:
 *
 * - `Forfeited` is green like `Paid`, because keeping the deposit as a
 *   cancellation fee means the practice has collected the money as intended.
 * - `Released` is neutral grey. A released hold is the ordinary, correct end of
 *   an attended appointment - nothing went wrong and nothing is owed.
 * - `Cancelled` is neutral grey too. The request has ended, and where another
 *   is needed the row's own actions offer it.
 *
 * Every status carries a subtle border in its own hue at the 200 shade, so a
 * column of chips reads as one family. `Authorised` alone stays at 300: the
 * stronger outline is its signal that a hold is not captured money.
 */
export const DEPOSIT_STATUS_COLOURS: Record<DepositStatus, string> = {
  [DepositStatus.Required]: `border border-orange-200 ${DEPOSIT_REQUIRED_COLOUR_CLASSES}`,
  [DepositStatus.Pending]: 'border border-blue-200 bg-blue-100 text-blue-800',
  [DepositStatus.Authorised]:
    'border border-amber-300 bg-amber-50 text-amber-900',
  [DepositStatus.Paid]: 'border border-green-200 bg-green-100 text-green-800',
  [DepositStatus.Forfeited]:
    'border border-green-200 bg-green-100 text-green-800',
  [DepositStatus.Refunded]: 'border border-gray-200 bg-gray-100 text-gray-800',
  [DepositStatus.Waived]: 'border border-gray-200 bg-gray-100 text-gray-800',
  [DepositStatus.Cancelled]: 'border border-gray-200 bg-gray-100 text-gray-800',
  [DepositStatus.Released]: 'border border-gray-200 bg-gray-100 text-gray-800',
};
